import { get as _get } from 'lodash-es'

import { get } from '@/api'
import {
  getModelIconClasses,
  mapModelType,
  modelNamePluralize,
  modelNameSingularize,
} from '@/utils'
import { cacheModelMeta, getCachedModelMeta } from '@/utils/modelMeta'
import { patchGlobalMeta } from '@/utils/modelMetaPatches'

function getDefaultState() {
  return {
    global: {
      apps: {},
    },
    model: {
      apps: {},
      annotationtypes: [],
    },
    view: {
      apps: {},
      annotationtypes: {
        detail: [],
        list: [],
      },
    },
  }
}

const state = getDefaultState()

const getters = {
  isMetaGlobalReady: (state) => {
    return Object.keys(state.global.apps).length > 0
  },
  isMetaModelReady: (state) => {
    return Object.keys(state.model.apps).length > 0
  },
  isMetaViewReady: (state) => {
    return Object.keys(state.view.apps).length > 0
  },
  isMetaReady: (state, getters) => {
    return (
      getters.isMetaGlobalReady &&
      getters.isMetaModelReady &&
      getters.isMetaViewReady
    )
  },
  getMeta: (state) => (app, name, type) => {
    const nameMapped = modelNameSingularize(name)
    const typeMapped = mapModelType(type)
    const model = _get(
      state,
      `model.apps.${app}.${nameMapped}.${typeMapped}`,
      {},
    )
    const view = _get(state, `view.apps.${app}.${nameMapped}.${typeMapped}`, {})
    return {
      model,
      view,
    }
  },
  getMetaGlobalApps: (state) => {
    return state.global.apps
  },
  metaGlobalApps: (state) => {
    return state.global.apps
  },
  getMetaGlobalModelInfo: (state) => (app, model) => {
    return _get(state.global.apps, [app, model], {})
  },
  getMetaGlobalModelAttrValue: (state) => (app, model, attr) => {
    return _get(state.global.apps, [app, model, attr], null)
  },
  getMetaModelApps: (state) => {
    return state.model.apps
  },
  metaModelApps: (state) => {
    return state.model.apps
  },
  getMetaModelNames: (state) => (app) => {
    return _get(state.model.apps, app, {})
  },
  getMetaModelTypes: (state) => (app, model) => {
    return _get(state.model.apps, [app, model], {})
  },
  getMetaModelType: (state) => (app, model, modeltype) => {
    return _get(state.model.apps, [app, model, mapModelType(modeltype)], {})
  },
  getMetaModelTypeAttrValue: (state) => (app, model, modeltype, attr) => {
    return _get(state.model.apps, [app, model, modeltype, attr], null)
  },
  annotationMetaModels: (state) => {
    return state.model.annotationtypes
  },
  isAnnotationMetalModelReady: (state, getters) => {
    return getters.annotationMetaModels.length > 0
  },
  annotationMetaView: (state) => {
    return state.view.annotationtypes
  },
  annotationMetaViews: (state, getters) => {
    return getters.annotationMetaView.list
  },
  getAnnotationMetaDetails: (state, getters) => {
    return getters.annotationMetaView.detail
  },
  getAnnotationMeta: (state, getters) => {
    return {
      model: getters.annotationMetaModels,
      view: getters.annotationMetaView,
    }
  },
  editableAnnotationsMetaModels(state, getters) {
    return getters.annotationMetaModels.filter((a) => !a.readonly)
  },
  getAnnotationMetaModelByName: (state, getters) => (name) => {
    return (
      getters.annotationMetaModels.find(
        (annotationMeta) => annotationMeta.name === name,
      ) || {}
    )
  },
  getAnnotationMetaViewDetailByName: (state, getters) => (name) => {
    return (
      getters.getAnnotationMetaDetails.find(
        (annotationMeta) => annotationMeta.name === name,
      ) || {}
    )
  },
  getOrderAttributes: (state, getters) => {
    return (
      getters.getMetaModelTypeAttrValue(
        'stocks',
        'order',
        'DEFAULT',
        'attributes',
      ) || {}
    )
  },
  getOrderedItemAttributes: (state, getters) => {
    return (
      getters.getMetaModelTypeAttrValue(
        'stocks',
        'ordereditem',
        'DEFAULT',
        'attributes',
      ) || {}
    )
  },
  modelTypes: (state, getters) => (app, name) => {
    const nameMapped = modelNameSingularize(name)
    const modelNameModelTypes = getters.getMetaModelTypes(app, nameMapped)
    return Object.keys(modelNameModelTypes).map((model) => {
      return {
        ...modelNameModelTypes[model],
        app_label: app,
        model_name: name,
        model_type: model,
      }
    })
  },
  allModelNames: (state, getters) => {
    const allModelNames = []
    for (let app of Object.keys(getters.getMetaModelApps)) {
      for (let modelName of Object.keys(getters.getMetaModelNames(app))) {
        const modelInfo = getters.getMetaGlobalModelInfo(app, modelName)
        const resolvable = _get(modelInfo, 'part_of_resolver', false)
        const label = _get(modelInfo, 'label')
        const verbose_label = _get(modelInfo, 'verbose_label')
        allModelNames.push({
          app: app,
          name: modelName,
          ...(label && { label }),
          ...(verbose_label && { verbose_label }),
          resolvable,
        })
      }
    }
    return allModelNames
  },
  modelNamesLabels: (state, getters) => {
    return getters.allModelNames.reduce((modelNamesLabels, model) => {
      modelNamesLabels[model.name] = model.verbose_label || model.label
      return modelNamesLabels
    }, {})
  },
  resolvableModels: (state, getters) => {
    return getters.allModelNames.filter((modelName) => {
      return modelName.resolvable === true
    })
  },
  getAllModelTypes: (state, getters) => {
    // const allApps = getters.getMetaModelApps()
    const getAllModelTypes = []
    for (let app of Object.keys(getters.getMetaModelApps)) {
      for (let modelName of Object.keys(getters.getMetaModelNames(app))) {
        getAllModelTypes.push(
          ...Object.keys(getters.getMetaModelTypes(app, modelName)).map(
            (modelType) => [app, modelName, modelType],
          ),
        )
      }
    }
    return getAllModelTypes
  },

  menuCategories: (state, getters) => {
    const processModelType = (app, modelName, modelType, modelTypeInfo) => {
      return {
        domain: modelTypeInfo?.domain,
        modelType: modelType.toLowerCase(),
        technology:
          modelTypeInfo?.technology || modelTypeInfo?.effective_technology,
        urlPath: [app, modelName, modelType],
        // for the create item button, whether the model needs to be created
        // with a payload (e.g. experiment need a name upon creation)
        needPayload: modelName === 'experiment' || modelName === 'protocol',
      }
    }
    return getters.getAllModelTypes.reduce(
      (menuCategories, [app, modelName, modelType]) => {
        const modelNameInfo = getters.getMetaGlobalModelInfo(app, modelName)
        let categoryLabel = modelNameInfo['menu']['category']
        if (categoryLabel === null) return menuCategories

        const modelTypeInfo = getters.getMetaModelType(
          app,
          modelName,
          modelType,
        )
        let categoryKey = categoryLabel.replace(/ +/gi, '').toLowerCase()

        let modelTypeLabel = modelTypeInfo['label']
        if (app === 'data_management') {
          categoryKey = 'datamanagement'
        } else if (categoryKey === 'collections') {
          categoryKey = modelName
          categoryLabel = modelNameInfo['verbose_label']
        } else if (modelName === 'experiment') {
          modelTypeLabel = modelNameInfo['label']
        }
        const categoryIconClasses =
          modelName === 'protocollist'
            ? getModelIconClasses(app, 'protocols')
            : getModelIconClasses(app, modelNamePluralize(modelName))
        if (Object.keys(menuCategories).includes(categoryKey)) {
          menuCategories[categoryKey]['modelTypes'].push({
            menuOrder: modelTypeInfo['menu_order'],
            modelTypeLabel: modelTypeLabel || modelType,
            modelTypeIconClasses: getModelIconClasses(
              app,
              modelNamePluralize(modelName),
            ),
            ...processModelType(app, modelName, modelType, modelTypeInfo),
          })
        } else {
          menuCategories[categoryKey] = {
            categoryLabel,
            categoryIconClasses,
            modelTypes: [
              {
                menuOrder: modelTypeInfo['menu_order'],
                modelTypeLabel: modelTypeLabel,
                modelTypeIconClasses: getModelIconClasses(
                  app,
                  modelNamePluralize(modelName),
                ),
                ...processModelType(app, modelName, modelType, modelTypeInfo),
              },
            ],
          }
        }
        return menuCategories
      },
      {},
    )
  },
  menuCategoryByModel: (state, getters) => {
    const categoryByModel = {}
    for (let category in getters.menuCategories) {
      const categoryModelTypes = getters.menuCategories[category].modelTypes
      for (let modelMenu in categoryModelTypes) {
        const [app, modelName, modelType] =
          categoryModelTypes[modelMenu].urlPath
        categoryByModel[`${app}${modelName}${modelType}`.toLowerCase()] =
          category
      }
    }
    return categoryByModel
  },
}

const actions = {
  reset({ commit }) {
    const defaultState = getDefaultState()
    commit('RESET_STATE', defaultState)
  },

  async fetchMeta({ dispatch, commit }, username) {
    let modelMeta = null
    const { cachedMeta, cacheIsValid } = getCachedModelMeta(username)
    if (cachedMeta && cacheIsValid) modelMeta = cachedMeta
    else {
      const url = `annotations/metadata/${username}/?format=json`
      const response = await get(url, { cancellable: false })
      const { status, data } = response || {}
      if (status === 200) {
        modelMeta = data.results
        cacheModelMeta(modelMeta, username)
      } else {
        const message = `
          Fatal error: cannot retrieve metadata.<br />
          If reloading the page doesn't help, contact your admins.
        `
        commit('notify', { type: 'error', message }, { root: true })
      }
    }
    if (modelMeta) dispatch('setModelMeta', modelMeta)
  },

  setModelMeta({ commit }, payload) {
    commit('SET_MODELMETA', {
      metaGlobal: payload.global,
      model: payload.model,
      view: payload.view,
    })
  },
}

const mutations = {
  RESET_STATE(state, defaultState) {
    Object.assign(state, defaultState)
  },
  SET_MODELMETA(state, { metaGlobal, model, view }) {
    state.global = metaGlobal
    patchGlobalMeta(state.global.apps)
    state.model = model
    state.view = view
  },
}

const meta = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}

export default meta
